import { useCallback, useMemo, useState } from 'react';
import {
    Stack,
    TextField,
    Dialog,
    DialogFooter,
    PrimaryButton,
    ScrollablePane,
    Dropdown,
    IDropdownOption,
    Toggle,
    TooltipHost,
    Label,
    ITooltipHostStyles,
    IconButton,
} from '@fluentui/react';
import produce from 'immer';
import { IntegrationCard } from '../IntegrationCard';
import { IntegrationEventType, TeamPlannerIntegration, TeamPlannerProjectType } from '../../../../../../api/generated/data-contracts';
import { useId } from '@fluentui/react-hooks';

export const TeamPlannerLayout = ({
    integration,
    setIntegration,
    isLoading,
}: {
    integration: TeamPlannerIntegration;
    setIntegration: React.Dispatch<React.SetStateAction<TeamPlannerIntegration>>;
    isLoading: boolean;
}) => {
    const { projectTypes, url } = integration.configuration;

    const [nameError, setNameError] = useState<string | null>(null);
    const [urlError, setUrlError] = useState<string | null>(null);
    const [columnsError, setColumnsError] = useState<any>(null);

    const validateName = (value: string) => {
        if (!value.trim()) {
            setNameError('Name is required.');
        } else {
            setNameError(null);
        }
    };

    const validateUrl = (value: string) => {
        const urlRegex = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/; // Basic URL validation
        if (!value.trim()) {
            setUrlError('URL is required.');
        } else if (!urlRegex.test(value)) {
            setUrlError('Please enter a valid URL (e.g., example.com).');
        } else {
            setUrlError(null);
        }
    };

    const toggleEvent = useCallback(
        (event: IntegrationEventType) => {
            setIntegration(integration => {
                return {
                    ...integration,
                    subscribedEvents: integration.subscribedEvents.includes(event)
                        ? integration.subscribedEvents.filter(sEvent => sEvent !== event)
                        : [...integration.subscribedEvents, event],
                };
            });
        },
        [setIntegration],
    );

    const projectTypeOptions: IDropdownOption[] = useMemo(
        () => [
            {
                key: 'Administrative' as TeamPlannerProjectType,
                text: 'Administrative',
            },
        ],
        [],
    );

    const tooltipHostStyles: Partial<ITooltipHostStyles> = {
        root: { display: 'inline-block', marginLeft: 4 }, // Adjust the margin as needed
    };

    const calloutProps = { gapSpace: 0 };
    const createAssignmentsTooltipId = useId('createAssignmentsTooltip');

    return (
        <Stack horizontal tokens={{ childrenGap: 10 }}>
            <Stack styles={{ root: { paddingLeft: 20, paddingBottom: 20, width: 350 } }}>
                <TextField
                    disabled={isLoading}
                    value={integration.name}
                    label="Name"
                    placeholder='E.g., "Team Planner Production"'
                    required
                    errorMessage={nameError}
                    onChange={(e, val) => {
                        setIntegration(
                            produce(integration => {
                                integration.name = val || '';
                            }),
                        );
                        validateName(val || '');
                    }}
                />
                <TextField
                    label="URL"
                    prefix="https://"
                    value={integration.configuration.url}
                    onChange={(e, val) => {
                        setIntegration(
                            produce(integration => {
                                integration.configuration.url = val || '';
                            }),
                        );
                        validateUrl(val || '');
                    }}
                    required
                    placeholder="teamplannerprod.azurewebsites.net"
                    errorMessage={urlError}
                />
            </Stack>

            <div style={{ width: '1px', backgroundColor: 'rgba(0,0,0,0.1)', flexGrow: 1 }} />

            <Stack styles={{ root: { width: '40vw', maxWidth: 1500, minHeight: 250, paddingRight: 20 } }}>
                <Stack styles={{ root: { position: 'relative', height: '100%', width: '100%' } }}>
                    <ScrollablePane>
                        <Stack styles={{ root: { gap: 10, flexWrap: 'flexGrow' } }} horizontal verticalAlign="start">
                            <IntegrationCard
                                eventType={'TeamPlannerAllocations'}
                                enabled={integration.subscribedEvents.includes('TeamPlannerAllocations')}
                                toggleEvent={toggleEvent}
                                tooltip="Sync allocations into Time for Teams"
                            >
                                <Toggle
                                    label={
                                        <Stack horizontal verticalAlign="center">
                                            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 0 }}>
                                                <Label>Create assignments</Label>
                                                <TooltipHost
                                                    id={createAssignmentsTooltipId}
                                                    calloutProps={calloutProps}
                                                    styles={tooltipHostStyles}
                                                    content={'Automatically create assignments for all tasks related to the project where time is allocated.'}
                                                >
                                                    <IconButton
                                                        iconProps={{ iconName: 'Info' }}
                                                        aria-describedby={createAssignmentsTooltipId}
                                                        ariaLabel="Info"
                                                        title="Info"
                                                    />
                                                </TooltipHost>
                                            </Stack>
                                        </Stack>
                                    }
                                    checked={integration.configuration.createAssignmentsForAllocations}
                                    onChange={(event: React.MouseEvent<HTMLElement>, checked?: boolean) => {
                                        setIntegration(
                                            produce(integration => {
                                                integration.configuration.createAssignmentsForAllocations = checked;
                                            }),
                                        );
                                    }}
                                />
                            </IntegrationCard>
                            <IntegrationCard
                                eventType="TeamPlannerProjects"
                                enabled={integration.subscribedEvents.includes('TeamPlannerProjects')}
                                toggleEvent={toggleEvent}
                                toggleDisabled={!integration.configuration.projectTypes || integration.configuration.projectTypes?.length < 1}
                                tooltip={
                                    integration.configuration.projectTypes?.length === 0
                                        ? 'Select at least one project type'
                                        : 'Sync projects into Time for Teams'
                                }
                            >
                                <Dropdown
                                    label="Project Type"
                                    required
                                    options={projectTypeOptions}
                                    multiSelect
                                    selectedKeys={integration.configuration.projectTypes}
                                    onChange={(event: React.FormEvent<HTMLDivElement>, item: IDropdownOption) => {
                                        setIntegration(
                                            produce(integration => {
                                                integration.configuration.projectTypes = item.selected ? [item.key as TeamPlannerProjectType] : [];
                                            }),
                                        );
                                    }}
                                    placeholder="Select Types"
                                />
                            </IntegrationCard>
                        </Stack>
                    </ScrollablePane>
                </Stack>
                <Dialog hidden={!columnsError} dialogContentProps={{ title: 'An error occurred' }}>
                    Couldn't communicate with Microsoft Entra ID. It's most likely an access issue with your account.
                    <DialogFooter>
                        <PrimaryButton onClick={() => setColumnsError(null)}>Okay</PrimaryButton>
                    </DialogFooter>
                </Dialog>
            </Stack>
        </Stack>
    );
};
